import { createUseStyles, withTheme } from '@glu/theming';
import PropTypes from 'prop-types';
import { AccessibilityText } from '@glu/utilities-react';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import {
  meridiemTo24Hour,
  meridiemLabelFromHour,
  twentyFourToMeridiem,
} from '../../utils/formatString.js';
import StyledSelect from './StyledSelect';
import styles from './TimePicker.styles';

const useStyles = createUseStyles(styles);
const TimePicker = ({
  className,
  dataQa,
  dateTime,
  disabled,
  hour,
  label,
  minute,
  second,
  setDateTime,
  timePicker24Hour,
  timePickerIncrement,
  timePickerSeconds,
  ...rest
}) => {
  const classes = useStyles(rest);

  const thisHour = timePicker24Hour ? hour : twentyFourToMeridiem(hour);

  const [meridiem, setmeridiem] = useState(meridiemLabelFromHour(hour));

  useEffect(() => setmeridiem(meridiemLabelFromHour(hour)), [hour]);

  const hourOptions = Array(12)
    .fill(0)
    .map((el, i) => i + 1);

  const hourOptions24 = Array(24)
    .fill(0)
    .map((el, i) => ({ label: i > 9 ? i : `0${i}`, value: i }));

  const minutes = Array(60)
    .fill(0)
    .map((el, i) => ({ label: i > 9 ? i : `0${i}`, value: i }))
    .filter((el, i) => i % timePickerIncrement === 0);

  const seconds = Array(60)
    .fill(0)
    .map((el, i) => ({ label: i > 9 ? i : `0${i}`, value: i }));

  const setTime = (hr, min, sec, mer) => {
    const adjustedHour = timePicker24Hour ? hr : meridiemTo24Hour(hr, mer);
    setDateTime(moment(dateTime).set('hour', adjustedHour).set('minute', min).set('second', sec));
  };

  const handleHourChange = (event) => {
    setTime(parseInt(event.target.value, 10), minute, second, meridiem);
  };

  const handleMinuteChange = (event) => {
    setTime(thisHour, parseInt(event.target.value, 10), second, meridiem);
  };

  const handleSecondChange = (event) => {
    setTime(thisHour, minute, parseInt(event.target.value, 10), meridiem);
  };

  const handlemeridiemChange = (event) => {
    setmeridiem(event.target.value);
    setTime(thisHour, minute, second, event.target.value);
  };

  return (
    <div className={classnames(classes.timepicker, className)} data-qa={dataQa}>
      <div className={classes.timepickerLabel}>{label}</div>

      <label htmlFor={`${dataQa}-hour-select`}>
        <AccessibilityText>{`${label} Hours`}</AccessibilityText>
        <StyledSelect>
          <select
            data-qa="hour-select"
            disabled={disabled}
            id={`${dataQa}-hour-select`}
            onChange={handleHourChange}
            value={thisHour}
          >
            {timePicker24Hour
              ? hourOptions24.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))
              : hourOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
          </select>
        </StyledSelect>
      </label>

      <label htmlFor={`${dataQa}-minute-select`}>
        <AccessibilityText>{`${label} Minutes`}</AccessibilityText>
        <StyledSelect>
          <select
            data-qa="minute-select"
            disabled={disabled}
            id={`${dataQa}-minute-select`}
            onChange={handleMinuteChange}
            value={minute}
          >
            {minutes.map((min) => (
              <option key={min.value} value={min.value}>
                {min.label}
              </option>
            ))}
          </select>
        </StyledSelect>
      </label>

      {timePickerSeconds && (
        <label htmlFor={`${dataQa}-second-select`}>
          <AccessibilityText>{`${label} Seconds`}</AccessibilityText>
          <StyledSelect>
            <select
              data-qa="second-select"
              disabled={disabled}
              id={`${dataQa}-second-select`}
              onChange={handleSecondChange}
              value={second}
            >
              {seconds.map((sec) => (
                <option key={sec.value} value={sec.value}>
                  {sec.label}
                </option>
              ))}
            </select>
          </StyledSelect>
        </label>
      )}
      {!timePicker24Hour && (
        <label htmlFor={`${dataQa}-meridiem-select`}>
          <AccessibilityText>{`${label} meridiem`}</AccessibilityText>
          <StyledSelect>
            <select
              data-qa="meridiem-select"
              disabled={disabled}
              id={`${dataQa}-meridiem-select`}
              onChange={handlemeridiemChange}
              value={meridiem}
            >
              <option value="am">am</option>
              <option value="pm">pm</option>
            </select>
          </StyledSelect>
        </label>
      )}
    </div>
  );
};

TimePicker.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  dataQa: PropTypes.string,
  dateTime: PropTypes.shape({}),
  disabled: PropTypes.bool,
  hour: PropTypes.number,
  label: PropTypes.string,
  minute: PropTypes.number,
  second: PropTypes.number,
  setDateTime: PropTypes.func.isRequired,
  timePicker24Hour: PropTypes.bool.isRequired,
  timePickerIncrement: PropTypes.number.isRequired,
  timePickerSeconds: PropTypes.bool.isRequired,
};

TimePicker.defaultProps = {
  className: '',
  dataQa: 'timepicker',
  dateTime: undefined,
  disabled: false,
  hour: 12,
  label: 'Time',
  minute: 0,
  second: 0,
};

export default withTheme(TimePicker);
